import { useState } from 'react';
import { UM } from '../_components';
import type { Building } from './types';

// ─── Types ───────────────────────────────────────────────────────────────────

type ConsoRow = { label: string; getValue: (y: number) => number | undefined };
type ConsoSection = { title: string; highlight?: boolean; rows: ConsoRow[] };

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STANDARD_YEAR = -1;
const YEARS = [STANDARD_YEAR, 2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018];
const yearLabel = (y: number) => (y === STANDARD_YEAR ? 'Standard' : String(y));

function fmtValue(v: number | undefined): string {
  return v !== undefined && Number.isFinite(v) ? v.toFixed(1) : '';
}

// ─── Collapsible section ─────────────────────────────────────────────────────

function ConsoSectionBlock({ title, highlight, disabled, rows }: {
  title: string; highlight?: boolean; disabled: boolean; rows: ConsoRow[];
}) {
  const [open, setOpen] = useState(!disabled);
  const LABEL_W = 200;
  const YEAR_W = 95;

  return (
    <div style={{ borderTop: `1px solid ${UM.border}`, padding: '12px 16px', background: highlight ? '#eff6ff' : undefined }}>
      <button
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, padding: 0, fontSize: 13, color: highlight ? '#1e40af' : UM.text }}
      >
        <span style={{ fontSize: 20, lineHeight: 1 }}>{open ? '▾' : '▸'}</span>
        <span style={{ fontWeight: 700 }}>{title}</span>
        <span style={{ color: UM.textMuted, fontWeight: 400 }}>(en MWhu)</span>
      </button>
      {open && !disabled && (
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 8, tableLayout: 'fixed', border: `1px solid ${UM.border}` }}>
          <colgroup>
            <col style={{ width: LABEL_W }} />
            {YEARS.map((y) => <col key={y} style={{ width: YEAR_W }} />)}
          </colgroup>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={{ padding: '5px 8px', color: '#374151' }}>{row.label}</td>
                {YEARS.map((y) => (
                  <td key={y} style={{ textAlign: 'right', padding: '5px 8px', fontWeight: y === STANDARD_YEAR ? 700 : 400, background: y === STANDARD_YEAR ? '#dbeafe' : undefined, borderLeft: '1px solid #e5e7eb' }}>
                    {fmtValue(row.getValue(y))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && disabled && <div style={{ marginTop: 4, fontSize: 12, color: UM.textMuted }}>Aucune donnée disponible</div>}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

interface ConsumptionsTabProps {
  building: Building;
}

export function ConsumptionsTab({ building }: ConsumptionsTabProps) {
  const baseHeat = building.heatConsumption / 1000;
  const baseCold = building.coldConsumption / 1000;
  const LABEL_W = 200;
  const YEAR_W = 95;

  const getData = (year: number) => {
    const v = year === STANDARD_YEAR ? 1 : 1 + Math.sin(year + building.id.length) * 0.08;
    return {
      heating: baseHeat * 0.7 * v,
      ecs: baseHeat * 0.3 * v,
      heatTotal: baseHeat * v,
      cold: baseCold * v,
      dju: year === STANDARD_YEAR ? 2350 : Math.round(2200 + Math.sin(year) * 150),
      djClim: year === STANDARD_YEAR ? 250 : Math.round(230 + Math.sin(year + 1) * 30),
    };
  };

  const sections: ConsoSection[] = [
    { title: 'Consommations retenues', highlight: true, rows: [
      { label: 'Chauffage', getValue: (y) => getData(y).heating },
      { label: 'ECS', getValue: (y) => getData(y).ecs },
      { label: 'Total chaud', getValue: (y) => getData(y).heatTotal },
      { label: 'Froid', getValue: (y) => baseCold > 0 ? getData(y).cold : undefined },
    ] },
    { title: 'Consommations renseignées', rows: [
      { label: 'Chauffage', getValue: () => undefined },
      { label: 'ECS', getValue: () => undefined },
      { label: 'Total chaud', getValue: () => undefined },
      { label: 'Froid', getValue: () => undefined },
    ] },
    { title: 'Consommations ENGIE Buildings & Enablers / Network', rows: [
      { label: 'Chauffage', getValue: (y) => y !== STANDARD_YEAR && y >= 2022 ? getData(y).heating * 0.95 : undefined },
      { label: 'ECS', getValue: (y) => y !== STANDARD_YEAR && y >= 2022 ? getData(y).ecs * 0.95 : undefined },
      { label: 'Total chaud', getValue: (y) => y !== STANDARD_YEAR && y >= 2022 ? getData(y).heatTotal * 0.95 : undefined },
      { label: 'PDL', getValue: (y) => y !== STANDARD_YEAR && y >= 2022 ? 1 : undefined },
    ] },
    { title: 'Consommations GRDF', rows: [
      { label: 'Chauffage', getValue: (y) => y !== STANDARD_YEAR && y >= 2020 ? getData(y).heating * 0.9 : undefined },
      { label: 'ECS', getValue: (y) => y !== STANDARD_YEAR && y >= 2020 ? getData(y).ecs * 0.9 : undefined },
      { label: 'Total Chaud', getValue: (y) => y !== STANDARD_YEAR && y >= 2020 ? getData(y).heatTotal * 0.9 : undefined },
      { label: 'PDL', getValue: (y) => y !== STANDARD_YEAR && y >= 2020 ? 2 : undefined },
    ] },
    { title: 'Consommations ENEDIS', rows: [
      { label: 'Chauffage', getValue: () => undefined },
      { label: 'ECS', getValue: () => undefined },
      { label: 'Total Chaud', getValue: () => undefined },
      { label: 'PDL', getValue: () => undefined },
    ] },
    { title: 'Consommations DLE Réseau', rows: [
      { label: 'Chauffage', getValue: (y) => y !== STANDARD_YEAR && y >= 2021 ? getData(y).heating * 1.02 : undefined },
      { label: 'ECS', getValue: (y) => y !== STANDARD_YEAR && y >= 2021 ? getData(y).ecs * 1.02 : undefined },
      { label: 'Total Chaud', getValue: (y) => y !== STANDARD_YEAR && y >= 2021 ? getData(y).heatTotal * 1.02 : undefined },
      { label: 'Froid', getValue: (y) => y !== STANDARD_YEAR && baseCold > 0 && y >= 2021 ? getData(y).cold : undefined },
      { label: 'PDL', getValue: (y) => y !== STANDARD_YEAR && y >= 2021 ? 1 : undefined },
    ] },
    { title: 'Consommations calculées (ratios par typologie et surface)', rows: [
      { label: 'Chauffage', getValue: (y) => getData(y).heating * 1.1 },
      { label: 'ECS', getValue: (y) => getData(y).ecs * 1.1 },
      { label: 'Total chaud', getValue: (y) => getData(y).heatTotal * 1.1 },
      { label: 'Froid', getValue: (y) => baseCold > 0 ? getData(y).cold * 1.1 : undefined },
    ] },
  ];

  const stdC: React.CSSProperties = { fontWeight: 700, background: '#dbeafe', textAlign: 'right', padding: '5px 8px', width: YEAR_W, borderLeft: `1px solid ${UM.border}` };
  const yearC: React.CSSProperties = { textAlign: 'right', padding: '5px 8px', width: YEAR_W, borderLeft: `1px solid ${UM.border}` };

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {/* DJU / DJClim header */}
      <div style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1, padding: '0 16px', borderBottom: `1px solid ${UM.border}` }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: LABEL_W }} />
            {YEARS.map((y) => <col key={y} style={{ width: YEAR_W }} />)}
          </colgroup>
          <thead>
            <tr style={{ background: UM.bgHeader }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: UM.textMuted, fontSize: 11 }}>Année</th>
              {YEARS.map((y) => (
                <th key={y} style={{ textAlign: 'center', padding: '6px 4px', fontWeight: y === STANDARD_YEAR ? 700 : 500, color: UM.text, fontSize: 11, background: y === STANDARD_YEAR ? '#dbeafe' : undefined, borderLeft: `1px solid ${UM.border}` }}>
                  {yearLabel(y)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: '4px 8px' }}>DJU</td>
              {YEARS.map((y) => <td key={y} style={y === STANDARD_YEAR ? stdC : yearC}>{getData(y).dju}</td>)}
            </tr>
            <tr>
              <td style={{ padding: '4px 8px' }}>DJClim</td>
              {YEARS.map((y) => <td key={y} style={y === STANDARD_YEAR ? stdC : yearC}>{getData(y).djClim}</td>)}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Consumption sections */}
      <div style={{ marginTop: -1 }}>
        {sections.map((s) => {
          const hasData = s.rows.some((r) => YEARS.some((y) => r.getValue(y) !== undefined));
          return (
            <ConsoSectionBlock
              key={s.title}
              title={s.title}
              highlight={s.highlight}
              disabled={!hasData}
              rows={s.rows}
            />
          );
        })}
      </div>
    </div>
  );
}
